/* lib/api/support.js — клиент поддержки (тикеты + вложения).
 * Файлы шлём multipart (FormData), иначе JSON. unwrap → data или throw.
 *
 * Гость (без авторизации) опознаётся по токену X-Support-Token: храним UUID в
 * localStorage и шлём заголовком на юзерские эндпоинты. Авторизованному бэкенд
 * токен игнорирует (скоупит по сессии), поэтому заголовок безопасно слать всегда. */
import { QUIET_POLL, apiFetch } from './client.js';
import { unwrap } from './unwrap.js';

const TOKEN_KEY = 'restorium.support_token';

function randToken() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* noop */ }
  return 'g-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function supportToken() {
  if (typeof window === 'undefined') return '';
  let t = window.localStorage.getItem(TOKEN_KEY);
  if (!t) { t = randToken(); window.localStorage.setItem(TOKEN_KEY, t); }
  return t;
}

const sup = (opts = {}) => ({
  ...opts,
  headers: { 'X-Support-Token': supportToken(), ...(opts.headers || {}) },
});

function form(body, files, extra) {
  const fd = new FormData();
  if (body) fd.append('body', body);
  if (extra) for (const [k, v] of Object.entries(extra)) if (v != null && v !== '') fd.append(k, v);
  (files || []).forEach((f) => fd.append('files', f));
  return fd;
}
const hasFiles = (files) => Array.isArray(files) && files.length > 0;

// ── пользователь / гость: свои заявки ────────────────────────────────────────
export const listMyTickets = () => unwrap(apiFetch('/api/support/tickets/', sup()));
// Число заявок, где ответ ещё не прочитан, — для бейджа в шапке. Отдельная ручка,
// а не поле списка: бейдж опрашивается раз в 30 с, и тянуть ради него всю
// переписку с вложениями незачем. Возвращает {ok,status,data} КАК ЕСТЬ (без
// unwrap): падение счётчика не должно ронять шапку — вызывающий читает res.ok.
// опрос только на чтение: по умолчанию вне счёта запросов в полёте (QUIET_POLL) — не держит и
// не сбрасывает переход вкладки в пространство, выбранное в соседней; req — переопределить
export const getSupportUnread = (req) => apiFetch('/api/support/unread/', sup({ ...QUIET_POLL, ...req }));
export const getMyTicket = (id) => unwrap(apiFetch(`/api/support/tickets/${id}/`, sup()));

// contact = { contact_name, contact_email, contact_phone } — телефон шлём и от
// гостя, и от зарегистрированного (у последнего он уедет в профиль, если пуст);
// имя/почта имеют смысл только для гостя.
export const createTicket = (subject, body, files, contact = {}) =>
  unwrap(apiFetch('/api/support/tickets/', sup({
    method: 'POST',
    body: hasFiles(files)
      ? form(body, files, { subject, ...contact })
      : { subject, body, ...contact },
  })));

export const sendMyMessage = (id, body, files) =>
  unwrap(apiFetch(`/api/support/tickets/${id}/`, sup({
    method: 'POST',
    body: hasFiles(files) ? form(body, files) : { body },
  })));

// ── оператор ─────────────────────────────────────────────────────────────────
export const listThreads = (status) =>
  unwrap(apiFetch('/api/support/threads/' + (status ? `?status=${status}` : '')));
export const getThread = (id) => unwrap(apiFetch(`/api/support/threads/${id}/`));

export const reply = (id, body, files) =>
  unwrap(apiFetch(`/api/support/threads/${id}/reply/`, {
    method: 'POST',
    body: hasFiles(files) ? form(body, files) : { body },
  }));

export const closeThread = (id, reason) =>
  unwrap(apiFetch(`/api/support/threads/${id}/close/`, { method: 'POST', body: { reason } }));

export const patchThread = (id, body) =>
  unwrap(apiFetch(`/api/support/threads/${id}/`, { method: 'PATCH', body }));
